import { startTransition, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { type DefaultValues, type FieldValues, type UseFormProps, type UseFormReturn, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

/** @description useFunnel 옵션 */
export type UseFunnelOptions<
  TContext extends FieldValues,
  TSteps extends string,
  TState extends Record<string, unknown> = Record<string, unknown>,
> = {
  /** 퍼널 식별자 */
  id: string;
  /** 퍼널 표시 이름 (e.g. '회원 근무지원 퍼널') — 다이어그램 자동 생성에 사용 */
  label: string;
  /** 퍼널의 base 경로 (e.g. '/work-apply', '/guest/work-apply') */
  basePath: string;
  /** 초기 step 및 퍼널 상태 초기값 */
  initial: { step: TSteps; state?: TState };
  /** react-hook-form defaultValues */
  defaultValues: DefaultValues<TContext>;
  /** react-hook-form 추가 옵션 (defaultValues 제외) */
  formOptions?: Omit<UseFormProps<TContext>, 'defaultValues'>;
  /** step 이름 → URL 경로 매핑 (e.g. { 지원_전_유의사항: 'guideline' }) */
  pathMap?: Record<TSteps, string>;
};

/** @description 퍼널 히스토리 네비게이션 */
export type FunnelHistory<TSteps extends string> = {
  push: (step: TSteps) => void;
  replace: (step: TSteps) => void;
  back: () => void;
  reset: () => void;
};

/** @description Render 컴포넌트에서 각 step이 받는 props */
export type StepRenderProps<
  TContext extends FieldValues,
  TSteps extends string,
  TState extends Record<string, unknown> = Record<string, unknown>,
> = {
  history: FunnelHistory<TSteps>;
  form: UseFormReturn<TContext>;
  state: TState;
  setState: React.Dispatch<React.SetStateAction<TState>>;
};

/** @description useFunnel 반환 타입 */
export type UseFunnelReturn<
  TContext extends FieldValues,
  TSteps extends string,
  TState extends Record<string, unknown> = Record<string, unknown>,
> = {
  step: TSteps;
  form: UseFormReturn<TContext>;
  history: FunnelHistory<TSteps>;
  state: TState;
  setState: React.Dispatch<React.SetStateAction<TState>>;
  Render: (
    props: Record<TSteps, (renderProps: StepRenderProps<TContext, TSteps, TState>) => React.ReactNode>
  ) => React.ReactNode;
  initial: { step: TSteps; state?: TState };
};

/**
 * @description basePath를 기준으로 pathname에서 step을 추출
 * @example extractStep('/work-apply', '/work-apply/guideline') → 'guideline'
 * @example extractStep('/work-apply', '/work-apply') → null
 * @example extractStep('/guest/work-apply', '/guest/work-apply/info') → 'info'
 */
function extractStep<TSteps extends string>(basePath: string, pathname: string): TSteps | null {
  const normalizedBase = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
  const normalizedPath = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

  if (!normalizedPath.startsWith(normalizedBase)) {
    return null;
  }

  const remaining = normalizedPath.slice(normalizedBase.length);
  if (!remaining || remaining === '/') {
    return null;
  }

  // '/guideline' → 'guideline'
  const step = remaining.startsWith('/') ? remaining.slice(1) : remaining;
  return (step || null) as TSteps | null;
}

export function useFunnel<
  TContext extends FieldValues,
  TSteps extends string,
  TState extends Record<string, unknown> = Record<string, unknown>,
>(options: UseFunnelOptions<TContext, TSteps, TState>): UseFunnelReturn<TContext, TSteps, TState> {
  const { initial, basePath: rawBasePath, defaultValues, formOptions, pathMap } = options;

  const basePath = rawBasePath.endsWith('/') ? rawBasePath.slice(0, -1) : rawBasePath;

  /** step 이름을 URL 경로로 변환 */
  const stepToPath = useCallback((step: TSteps): string => pathMap?.[step] ?? step, [pathMap]);
  /** URL 경로를 step 이름으로 변환 */
  const pathToStep = useCallback(
    (path: string): TSteps | null => {
      if (!pathMap) return path as TSteps;
      const entry = Object.entries(pathMap).find(([, p]) => p === path);
      return entry ? (entry[0] as TSteps) : null;
    },
    [pathMap]
  );

  const initialState = (initial?.state ?? {}) as TState;
  const [step, setStep] = useState<TSteps>(initial.step);
  const [state, setState] = useState<TState>(initialState);

  const form = useForm<TContext>({
    mode: 'onChange',
    ...formOptions,
    defaultValues,
  });

  const navigate = useNavigate();

  // ─── 경로 트래킹 (DevTools 다이어그램용) ───
  const visitedPathRef = useRef<string[]>([stepToPath(initial.step)]);

  const dispatchPath = useCallback(() => {
    const path = [...visitedPathRef.current];
    try {
      sessionStorage.setItem(`funnel:path:${options.id}`, JSON.stringify(path));
    } catch (e) {
      console.error('Failed to store funnel path', e);
    }
    window.dispatchEvent(new CustomEvent('funnel:path', { detail: { id: options.id, path } }));
  }, [options.id]);

  const trackStep = useCallback(
    (nextStep: TSteps) => {
      const nextPath = stepToPath(nextStep);
      const idx = visitedPathRef.current.indexOf(nextPath);
      if (idx !== -1) {
        visitedPathRef.current = visitedPathRef.current.slice(0, idx + 1);
      } else {
        visitedPathRef.current = [...visitedPathRef.current, nextPath];
      }
      dispatchPath();
    },
    [stepToPath, dispatchPath]
  );

  // 항상 초기 step으로 replace 이동 (중간 진입 방지)
  // 개발환경에서 ?dev=1 쿼리파람이 있으면 현재 URL의 step을 유지 (중간 진입 허용)
  useLayoutEffect(() => {
    if (process.env.ENVIRONMENT === 'development' && new URLSearchParams(window.location.search).get('dev') === '1') {
      const devPath = extractStep<string>(basePath, window.location.pathname);
      const devStep = devPath ? pathToStep(devPath) : null;
      if (devStep) {
        setStep(devStep);
        visitedPathRef.current = [stepToPath(devStep)];
        dispatchPath();
        return;
      }
    }
    navigate(`${basePath}/${stepToPath(initial.step)}`, { replace: true });
    dispatchPath();
  }, []);

  // popstate 리스너 (브라우저 뒤로가기 처리)
  useEffect(() => {
    const handlePopState = () => {
      const nextPath = extractStep<string>(basePath, window.location.pathname);
      const nextStep = nextPath ? pathToStep(nextPath) : null;
      setStep(nextStep ?? initial.step);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [initial.step]);

  // 외부 컴포넌트에서 funnel:reset 이벤트로 reset 호출
  useEffect(() => {
    const handleFunnelReset = () => history.reset();

    window.addEventListener('funnel:reset', handleFunnelReset);
    return () => window.removeEventListener('funnel:reset', handleFunnelReset);
  }, []);

  const history: FunnelHistory<TSteps> = useMemo(
    () => ({
      push: (nextStep: TSteps) => {
        trackStep(nextStep);
        // setStep과 navigate를 같은 transition에 배치해 URL과 step 상태가 동시에 업데이트되도록 보장
        startTransition(() => {
          setStep(nextStep);
          navigate(`${basePath}/${stepToPath(nextStep)}`);
        });
      },
      replace: (nextStep: TSteps) => {
        trackStep(nextStep);
        startTransition(() => {
          setStep(nextStep);
          navigate(`${basePath}/${stepToPath(nextStep)}`, { replace: true });
        });
      },
      back: () => {
        navigate(-1);
      },
      reset: () => {
        visitedPathRef.current = [stepToPath(initial.step)];
        dispatchPath();
        form.reset(defaultValues);
        startTransition(() => {
          setStep(initial.step);
          setState(initialState);
          navigate(`${basePath}/${stepToPath(initial.step)}`, { replace: true });
        });
      },
    }),
    [navigate, trackStep, dispatchPath]
  );

  const Render = useCallback(
    (props: Record<TSteps, (renderProps: StepRenderProps<TContext, TSteps, TState>) => React.ReactNode>) => {
      const renderFn = props[step];
      if (!renderFn) return null;
      return renderFn({ history, form, state, setState });
    },
    [step, history, form, state, setState]
  );

  return { step, form, history, state, setState, Render, initial };
}
